import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { CheckOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getErrorMessage } from "@/utilities";
import {
  createFiservInstallment,
  deleteFiservInstallment,
  listFiservInstallments,
  updateFiservInstallment,
} from "./api";
import {
  FiservCardType,
  FiservInstallment,
  FiservInstallmentInput,
} from "./types";
import { ENCABEZADO_DE_TABLA, PAGINADOR, confirmarBorrado } from "./ui";

const { Text } = Typography;

interface Props {
  sellerCode: string;
  cards: FiservCardType[];
}

const money = (n: number) => (n ? `$${n.toLocaleString("es-AR")}` : "—");

/**
 * Las cuotas de Fiserv del seller (#635): las filas de `firstdata_installments` con su
 * vendor_id. Si el seller no tiene ninguna para una tarjeta, el checkout usa la grilla global
 * de Magento, que no se edita desde acá. En pantalla se llama "las cuotas generales": el seller
 * no sabe qué es Magento (xcons-docs#636).
 */
export const FiservInstallments: React.FC<Props> = ({ sellerCode, cards }) => {
  const [rows, setRows] = useState<FiservInstallment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FiservInstallment | "new" | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [cardFilter, setCardFilter] = useState<string | undefined>();
  const [form] = Form.useForm<FiservInstallmentInput>();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listFiservInstallments(sellerCode));
    } catch {
      message.error("No se pudieron cargar las cuotas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sellerCode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const open = (row: FiservInstallment | null) => {
    setEditing(row ?? "new");
    form.resetFields();
    form.setFieldsValue(
      row ?? { card: cardFilter, interest: 0, fromAmount: 0, toAmount: 0 },
    );
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing === "new") await createFiservInstallment(sellerCode, values);
      else if (editing)
        await updateFiservInstallment(sellerCode, editing.id, values);
      message.success("Cuota guardada");
      setEditing(null);
      load();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteFiservInstallment(sellerCode, id);
      message.success("Cuota eliminada");
      setEditing(null);
      load();
    } catch (err: any) {
      message.error(getErrorMessage(err));
    }
  };

  const shown = cardFilter ? rows.filter((r) => r.card === cardFilter) : rows;

  return (
    <>
      <Space
        style={{
          marginBottom: 12,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Select
          allowClear
          placeholder="Todas las tarjetas"
          style={{ minWidth: 240 }}
          value={cardFilter}
          onChange={setCardFilter}
          options={cards.map((c) => ({ value: c.code, label: c.label }))}
        />
        <Button icon={<PlusOutlined />} onClick={() => open(null)}>
          Nueva cuota
        </Button>
      </Space>

      <Table<FiservInstallment>
        rowKey="id"
        // Como las tablas de Online y Offline: con borde, y la fila se abre con un clic
        // (xcons-docs#636).
        className="tabla-con-borde-redondeado"
        bordered
        size="small"
        components={ENCABEZADO_DE_TABLA}
        loading={loading}
        dataSource={shown}
        pagination={PAGINADOR}
        onRow={(r) => ({
          style: { cursor: "pointer" },
          onClick: () => open(r),
        })}
        locale={{
          emptyText: "Sin cuotas propias: se usan las cuotas generales",
        }}
        columns={[
          { title: "Tarjeta", dataIndex: "cardLabel" },
          { title: "Cuotas", dataIndex: "installmentNumber", width: 80 },
          {
            title: "Interés",
            dataIndex: "interest",
            width: 90,
            render: (v) => `${v}%`,
          },
          {
            title: "Texto",
            dataIndex: "name",
            render: (v) => v ?? <Text type="secondary">—</Text>,
          },
          {
            title: "Desde",
            dataIndex: "fromAmount",
            width: 110,
            render: money,
          },
          { title: "Hasta", dataIndex: "toAmount", width: 110, render: money },
        ]}
      />

      <Modal
        open={!!editing}
        title={editing === "new" ? "Nueva cuota" : "Editar cuota"}
        onCancel={() => setEditing(null)}
        destroyOnClose
        // Sin columna de acciones: borrar es desde la cuota, a la izquierda del pie, como
        // Eliminar en el panel de un método offline.
        footer={
          <Flex justify="space-between">
            {editing && editing !== "new" ? (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  confirmarBorrado(
                    `¿Eliminar la opción de ${editing.installmentNumber} cuotas de ${editing.cardLabel}?`,
                    () => handleDelete(editing.id),
                  )
                }
              >
                Eliminar
              </Button>
            ) : (
              <span />
            )}
            <Space>
              <Button onClick={() => setEditing(null)}>Cancelar</Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                iconPosition="end"
                loading={saving}
                onClick={handleSave}
              >
                Guardar
              </Button>
            </Space>
          </Flex>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Tarjeta"
                name="card"
                rules={[{ required: true, message: "Elegí la tarjeta" }]}
              >
                <Select
                  options={cards.map((c) => ({
                    value: c.code,
                    label: c.label,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Cuotas"
                name="installmentNumber"
                rules={[{ required: true, message: "Cantidad" }]}
              >
                <InputNumber min={1} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Interés %"
                name="interest"
                rules={[{ required: true, message: "Interés" }]}
              >
                <InputNumber
                  min={0}
                  step={0.5}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Texto para el cliente" name="name">
            <Input placeholder='Ej. "Sin interés" o "Con 15% de interés"' />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                label="Monto desde"
                name="fromAmount"
                extra="0 = sin mínimo"
              >
                <InputNumber
                  min={0}
                  precision={0}
                  prefix="$"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Monto hasta"
                name="toAmount"
                extra="0 = sin máximo"
              >
                <InputNumber
                  min={0}
                  precision={0}
                  prefix="$"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Código Fiserv"
                name="installmentCode"
                extra="Vacío = igual a la cantidad de cuotas."
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
